import { useMemo } from "react";
import useStore from "../../../helpers/useStore";
import { CalendarContext } from "../../../stores/Calendar/calendar.provider";
import { AppointmentDto, PatientDto, TherapistListType, UserDto } from "../../../api-client";
import {
  useUnscheduledPatientAppointments,
  useUnscheduledContinuousAppointments,
} from "../../../stores/Calendar/calendar.queries";
import {
  useCancelledAppointmentsByPatient,
  useTherapistListByPatient,
} from "../../../stores/Appointment/appointment.queries";
import { useAllUsersBasic } from "../../../stores/User/user.queries";
import { usePatientNotes } from "../../../stores/Notes/notes.queries";

// Helper function to get a display name for a therapist, falling back to username or "Unbekannt" if no name is available.
const therapistDisplayName = (u?: UserDto): string => {
  if (!u) return "Unbekannt";
  const full = `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim();
  return full || u.nickName || u.username || "Unbekannt";
};

/**
 * Controller hook that orchestrates UI params + data + business logic for EventShelf
 * Follows the calendar refactor pattern: UI params from MobX, data from react-query
 *
 * Manages two types of shelves:
 * 1. Unscheduled appointments shelf - patient-specific open appointments (from react-query)
 * 2. PinBoard ("Ablage") - temporary holder for appointments, managed in CalendarStore (MobX)
 */
export const useEventShelfController = () => {
  const CalendarStore = useStore(CalendarContext);

  // UI Parameters from MobX Store
  const { continuousView, selectedPatientId, pinBoardItems } = CalendarStore;

  // Data from react-query hooks
  const { data: unscheduledRegularAppointments = [], isLoading: isLoadingRegular } =
    useUnscheduledPatientAppointments(selectedPatientId, !!selectedPatientId);
  const { data: cancelledAppointments = [], isLoading: isLoadingCancelled } =
    useCancelledAppointmentsByPatient(selectedPatientId!);
  const { data: unscheduledContinuousData = [], isLoading: isLoadingContinuous } =
    useUnscheduledContinuousAppointments(selectedPatientId, !!selectedPatientId);

  // Patient notes from react-query (read-only display)
  const { data: patientNotes = [] } = usePatientNotes(selectedPatientId, !!selectedPatientId);

  // Therapist white/black-list for the selected patient (read-only display on the shelf)
  const { data: therapistListRaw = [] } = useTherapistListByPatient(
    Number(selectedPatientId),
    !!selectedPatientId
  );
  const { data: allUsers = [] } = useAllUsersBasic();
  const therapistList = useMemo(() => {
    const userById = new Map<string, UserDto>();
    for (const u of allUsers) if (u.id) userById.set(u.id, u);
    return therapistListRaw
      .map((entry) => {
        const user = entry.therapistId ? userById.get(entry.therapistId) : undefined;
        return {
          key: entry.id ?? entry.therapistId,
          listType: entry.listType,
          isMandatory: !!entry.isMandatory,
          name: therapistDisplayName(user),
        };
      })
      // Gewünscht (WHITELIST) before nicht gewünscht, Muss before Kann,
      // alphabetical within each group.
      .sort((a, b) => {
        if (a.listType !== b.listType)
          return a.listType === TherapistListType.Whitelist ? -1 : 1;
        if (a.isMandatory !== b.isMandatory) return a.isMandatory ? -1 : 1;
        return a.name.localeCompare(b.name);
      });
  }, [therapistListRaw, allUsers]);

  // PinBoard data from MobX store (client-side state)
  const pinBoardAppointments = pinBoardItems?.filter((item: any) => item.timeSlots) || [];

  const unscheduledCancelledAppointments = [
    ...unscheduledRegularAppointments,
    ...cancelledAppointments?.map((cancelled: any) => {
      // Add the cancelled appointment to unscheduled if necessary
      return {
        ...cancelled.appointment, // Add appointment data from the cancelled one
        resolved: cancelled.resolved,
        byTherapist: cancelled.byTherapist,
        originalDate: cancelled.originalDate,
      };
    }),
  ];

  // console.debug(
  //   "EventShelfController: unscheduledCancelledAppointments count:",
  //   unscheduledCancelledAppointments.length
  // );

  // Select the appropriate unscheduled data based on view mode
  const unscheduledAppointments = continuousView
    ? unscheduledContinuousData
    : unscheduledCancelledAppointments;
  const patient: PatientDto | undefined = selectedPatientId
    ? ({ id: selectedPatientId } as PatientDto)
    : undefined;
  const isLoading = isLoadingRegular || isLoadingContinuous || isLoadingCancelled;

  // Computed values
  const hasPatient = !!patient;
  const hasUnscheduledAppointments = unscheduledAppointments.length > 0;
  const hasPinBoardEvents =
    pinBoardAppointments.length > 0 ;
  const shouldShowUnscheduled = hasPatient && hasUnscheduledAppointments;
  const shouldShowPinBoard = hasPinBoardEvents;

  // Event Shelf configurations
  const unscheduledShelfConfig = useMemo(
    () => ({
      appointments: unscheduledAppointments,
      title: `Offene ${continuousView ? "Dauertermin" : "Termine"}`,
      patient,
      draggable: true,
      closeButton: false,
      visible: shouldShowUnscheduled,
      mode: "unscheduled" as const,
    }),
    [
      unscheduledAppointments,
      continuousView,
      patient,
      shouldShowUnscheduled,
      unscheduledContinuousData,
    ]
  );

  const pinBoardConfig = useMemo(
    () => ({
      appointments: pinBoardAppointments,
      title: "Ablage", // Temporary pinboard for appointments
      patient: undefined, // PinBoard doesn't need patient context
      draggable: true,
      closeButton: true,
      visible: shouldShowPinBoard,
      mode: "pinboard" as const,
    }),
    [continuousView,  pinBoardAppointments, shouldShowPinBoard]
  );

  const getAddressPayload = (appointment: AppointmentDto | undefined) => {
    if (!appointment?.id) return null;

    const nestedCoordinates = (appointment as any)?.address?.coordinates;
    if (nestedCoordinates?.latitude != null && nestedCoordinates?.longitude != null) {
      return {
        id: (appointment as any).address.id,
        latitude: nestedCoordinates.latitude,
        longitude: nestedCoordinates.longitude,
      };
    }
    return null;
  };

  // Handlers
  const handleSelectEvent = (eventId: string) => {
    // Toggle: if already selected, deselect
    if (CalendarStore.selectedShelfAppointmentId === eventId) {
      CalendarStore.setSelectedShelfAppointment(null, null);
      return;
    }

    // Find the appointment across all shelf configs to extract address coordinates
    const allAppts = [
      ...unscheduledAppointments,
      ...pinBoardAppointments,
    ] as AppointmentDto[];
    const appointment = allAppts.find((a) => a.id === eventId);
    const addressPayload = getAddressPayload(appointment);

    CalendarStore.setSelectedShelfAppointment(eventId, addressPayload);
  };

  const handleClosePinBoard = () => {
    // Clear the pinBoard items from the store
    CalendarStore.clearPinBoard();
  };

  const handleAddToPinBoard = (item: AppointmentDto | any) => {
    CalendarStore.addToPinBoard(item);
  };

  const handleRemoveFromPinBoard = (itemId: string) => {
    CalendarStore.removeFromPinBoard(itemId);
  };

  // Both sections are co-visible: the Ablage (pinboard) renders NEXT TO the
  // unscheduled shelf in the planning band instead of replacing it (the old
  // behavior hid a patient's open appointments mid-"Umziehen").
  const shelves = [unscheduledShelfConfig, pinBoardConfig].filter((shelf) => shelf.visible);
  const allShelvesData = [unscheduledShelfConfig, pinBoardConfig]; // GroupAppointment needs non-visible shelves too

  const shelfIsOpen =
    shelves.length > 0 && shelves[0].visible && shelves[0].appointments.length > 0;
  // if (shelfIsOpen && !CalendarStore.eventShelfIsOpen) {
  //   CalendarStore.setEventShelfOpen(true);
  // }
  return {
    // Configurations for rendering multiple EventShelf instances
    shelves, // Only return visible shelves
    allShelvesData, // Return all shelves data for debugging or other purposes

    // Handlers
    handleSelectEvent,
    handleCloseShelf: handleClosePinBoard,
    handleAddToPinBoard,
    handleRemoveFromPinBoard,

    // Loading states (from query hooks)
    isLoading,
    // isOpen state (from MobX store)
    isOpen: shelfIsOpen,

    // UI state
    hasPatient,
    patientNotes,
    therapistList,
    continuousView,
  };
};
